import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/producto.css';
import ProductosRelacionados from './ProductoRelacionados';
import Seo from './Seo';
import { absoluteUrl } from '../config/seo';
import { useCarrito } from '../context/carritoContext';
import { getProductById, getPurchasedTogether, getVarianteDefault } from '../services/catalogApi';
import { idDesdeSlug, productoPath } from '../utils/slug';
import {
  tieneVariantes,
  getVariantesProducto,
  getVariantePorId,
} from '../services/catalogApi';

const PaginaProducto = () => {
  const { slug } = useParams();
  const id = idDesdeSlug(slug);
  const [searchParams, setSearchParams] = useSearchParams();
  const [producto, setProducto] = useState(null);
  const [varianteId, setVarianteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agregadoIds, setAgregadoIds] = useState([]);
  const [compradosJuntos, setCompradosJuntos] = useState([]);

  const navigate = useNavigate();
  const { agregarAlCarrito, carrito } = useCarrito();

  const handleBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Evita que la respuesta de un producto anterior (todavía en vuelo al
    // navegar rápido de un producto a otro) sobreescriba el estado del
    // producto que se está pidiendo ahora.
    let vigente = true;

    const cargarProducto = async () => {
      setLoading(true);
      try {
        const varInicial = searchParams.get('var') || undefined;
        const productoEncontrado = await getProductById(id, varInicial);
        if (!vigente) return;

        if (!productoEncontrado) {
          setError('Producto no encontrado');
          setProducto(null);
          return;
        }

        // Si el slug de la URL no coincide con el nombre real del producto
        // (link viejo tipo /producto/1, o el nombre cambió), se corrige la
        // URL sin recargar la página, para que el navegador/Google terminen
        // viendo siempre la versión canónica /producto/nombre-del-producto-<artId>.
        // Se compara contra ESTE resultado (no contra el estado `producto`
        // previo) para no disparar una redirección con datos de otro producto
        // mientras la carga está en curso.
        const rutaCorrecta = productoPath(productoEncontrado);
        if (`/producto/${slug}` !== rutaCorrecta) {
          navigate(
            { pathname: rutaCorrecta, search: searchParams.toString() ? `?${searchParams.toString()}` : '' },
            { replace: true }
          );
          return; // el cambio de slug vuelve a disparar este efecto con la ruta correcta
        }

        setProducto(productoEncontrado);
        setVarianteId(productoEncontrado.varianteActiva.varId);
      } catch {
        if (vigente) setError('No se pudo cargar el producto');
      } finally {
        if (vigente) setLoading(false);
      }
    };

    cargarProducto();
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, slug]);

  useEffect(() => {
    if (!producto) return;
    getPurchasedTogether({ articuloActual: producto.artId, limit: 6 })
      .then(items => setCompradosJuntos(items.map(item => ({ producto: item, variante: getVarianteDefault(item) })).filter(item => item.variante)))
      .catch(() => setCompradosJuntos([]));
  }, [producto]);

  const handleSeleccionarVariante = (variante) => {
    setVarianteId(variante.varId);
    setSearchParams(
      variante.etiqueta ? { var: variante.varId } : {},
      { replace: true }
    );
  };

  const handleAgregarCarrito = (e, productoActual, variante) => {
    e.stopPropagation();

    const yaEnCarrito = carrito.some((p) => p.artId === variante.varId);
    if (yaEnCarrito) return;

    const itemCarrito = {
      ...productoActual,
      artId: variante.varId,
      artNom: variante.etiqueta ? `${productoActual.artNom} - ${variante.etiqueta}` : productoActual.artNom,
      artPrecio: variante.artPrecio,
      artDescuento: variante.artDescuento,
      artFoto: variante.artFoto,
    };
    delete itemCarrito.variantes;
    delete itemCarrito.varianteActiva;

    agregarAlCarrito(itemCarrito);

    setAgregadoIds((prev) => [...prev, variante.varId]);

    const evento = new Event('carrito-actualizado');
    window.dispatchEvent(evento);

    setTimeout(() => {
      setAgregadoIds((prev) => prev.filter((itemId) => itemId !== variante.varId));
    }, 2000);
  };

  if (loading) return <p>Cargando producto...</p>;
  if (error) return <p>{error}</p>;
  if (!producto) return <p>Producto no encontrado</p>;

  const variantes = getVariantesProducto(producto);
  const varianteActiva = getVariantePorId(producto, varianteId) || variantes[0];
  const foto = varianteActiva.artFoto?.[0] ?? varianteActiva.artFoto ?? '/img/default.jpg';

  const varianteActivaEnCarrito = carrito.some((p) => p.artId === varianteActiva.varId);
  const mostrarAgregadoActiva = agregadoIds.includes(varianteActiva.varId) || varianteActivaEnCarrito;

  const productoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: producto.artNom,
    description: producto.artDescrip,
    image: foto,
    offers: {
      '@type': 'Offer',
      url: absoluteUrl(productoPath(producto)),
      priceCurrency: 'MXN',
      price: Math.max(0, varianteActiva.artPrecio - varianteActiva.artDescuento).toFixed(2),
      availability: 'https://schema.org/InStock',
    },
  };

  return (
    <section className="producto-page">
      <Seo
        title={producto.artNom}
        description={producto.artDescrip}
        path={productoPath(producto)}
        image={foto}
        type="product"
        jsonLd={productoJsonLd}
      />

      <button className="back-btn" onClick={handleBackClick}>
        &#8249;
      </button>

      <div className="producto-detalle">
        <div className="producto-media">
          <img
            src={foto}
            alt={producto.artNom}
            className="producto-imagen"
          />
        </div>

        <div className="producto-info-detalle">
          <p className="producto-kicker">Detalle del producto</p>
          <h1>{producto.artNom}</h1>

          <div className="pag-precios">
            {varianteActiva.artDescuento > 0 ? (
              <>
                <p className="pag-precio-original">
                  ${varianteActiva.artPrecio.toFixed(2)}
                </p>
                <p className="pag-precio-descuento">
                  ${(varianteActiva.artPrecio - varianteActiva.artDescuento).toFixed(2)}
                </p>
              </>
            ) : (
              <p className="pag-precio-normal">
                ${varianteActiva.artPrecio.toFixed(2)}
              </p>
            )}
          </div>

          <p className="producto-descripcion">{producto.artDescrip}</p>

          {(producto.tagObjects || []).map((tag) => {
            return (
              <button
                key={tag.tagId}
                type="button"
                className="producto-tag-chip"
                onClick={() => navigate(`/tienda?tagId=${tag.tagId}`)}
              >
                {tag.tagNombre}
              </button>
            );
          })}

          <br />

          {tieneVariantes(producto) && (
            <div className="producto-variantes">
              <p className="producto-variantes-label">
                {producto.varianteTipo || 'Opciones'}
              </p>
              <div className="producto-variantes-lista">
                {variantes.map((variante) => (
                  <button
                    key={variante.varId}
                    type="button"
                    className={`producto-variante-chip ${
                      varianteActiva.varId === variante.varId ? 'activo' : ''
                    }`}
                    onClick={() => handleSeleccionarVariante(variante)}
                  >
                    {variante.etiqueta}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            className={`producto-buy-btn ${mostrarAgregadoActiva ? "is-added" : ""}`}
            onClick={(e) => handleAgregarCarrito(e, producto, varianteActiva)}
            disabled={mostrarAgregadoActiva}
          >
            {mostrarAgregadoActiva ? "Ya está en el carrito" : "Agregar al carrito"}
          </button>
          {compradosJuntos.length > 0 && (
            <div className="comprados-juntos">
              <h3>Se suelen comprar juntos</h3>
              <div className="comprados-juntos-lista">
                {compradosJuntos.map(({ producto: itemProducto, variante: varianteItem }) => {
                  const fotoItem = varianteItem.artFoto?.[0] ?? varianteItem.artFoto ?? '/img/default.jpg';
                  const yaEnCarrito = carrito.some((p) => p.artId === varianteItem.varId);
                  const mostrarAgregado = agregadoIds.includes(varianteItem.varId) || yaEnCarrito;

                  return (
                    <div
                      key={`${itemProducto.artId}-${varianteItem.varId}`}
                      className="comprado-item"
                      onClick={() => navigate(
                        varianteItem.etiqueta
                          ? `${productoPath(itemProducto)}?var=${varianteItem.varId}`
                          : productoPath(itemProducto)
                      )}
                    >
                      <img src={fotoItem} alt={itemProducto.artNom} loading="lazy" />
                      <div className="comprado-info">
                        <span>
                          {itemProducto.artNom}
                          {varianteItem.etiqueta ? ` - ${varianteItem.etiqueta}` : ''}
                        </span>
                        <strong>${varianteItem.artPrecio.toFixed(2)}</strong>
                      </div>
                      <button
                        type="button"
                        className={mostrarAgregado ? 'is-added' : ''}
                        disabled={mostrarAgregado}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAgregarCarrito(e, itemProducto, varianteItem);
                        }}
                      >
                        {mostrarAgregado ? '✓' : '+'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ul className="producto-trust">
            <li>Compra segura</li>
            <li>Atención personalizada</li>
            <li>Entrega local disponible</li>
          </ul>
        </div>
      </div>

      <section className="productos-relacionados">
        <ProductosRelacionados productoId={producto.artId} />
      </section>
    </section>
  );
};

export default PaginaProducto;
